/** What the app shows while it starts: a spinner, first-run setup, sign-in, or the mail. */
export type Boot =
  | { state: "loading" }
  | { state: "signed-out"; appName: string }
  | { state: "setup"; appName: string; tokenConfigured: boolean }
  | { state: "ready" };

/**
 * A request was refused for want of a session: the sign-in page shows. Not over first-run setup,
 * though (there's no account yet to sign in to), nor over the sign-in page already showing (with
 * the name the server gave it): starting fetches several things at once, and one refused after
 * the app has asked the server which of the two to show must leave that answer as it is.
 */
export const signedOut = (current: Boot, appName: string): Boot =>
  current.state === "setup" || current.state === "signed-out"
    ? current
    : { state: "signed-out", appName };

/** How long the app waits before it tries to start again after a failure. */
const RETRY_MS = 5_000;
/** The longest it waits between tries while the server keeps refusing. */
const MAX_RETRY_MS = 5 * 60_000;

/**
 * The wait before the next try to start, after `refusals` refusals in a row before this failure.
 * Offline, or an error that may pass: a few seconds. Refused for too many requests (429): twice
 * as long after each refusal in a row, and never sooner than the server's Retry-After, so tabs
 * waiting at one address (an office's) don't keep it over the limit by asking again.
 */
export const retryDelay = (
  failure: { status?: number; retryAfter?: number | null } | null,
  refusals: number,
): number => {
  if (failure?.status !== 429) return RETRY_MS;
  const backoff = Math.min(RETRY_MS * 2 ** refusals, MAX_RETRY_MS);
  return Math.max(backoff, (failure.retryAfter ?? 0) * 1000);
};
